import {createSelector} from '@reduxjs/toolkit';
import {AnyAction} from 'redux';

import {GLOBAL_LOADING, RESET} from '~redux/types';
import {GlobalLoadingType, ReduxStateGlobal} from '~redux/types/utils';
import {ReduxState} from '~redux/index';

export const initialState: ReduxStateGlobal = {
  loadingCounter: 0,
  loadingType: undefined,
};

export interface GlobalLoadingPayload {
  counter: number;
  loadingType?: GlobalLoadingType;
}
export interface UtilAction extends AnyAction {
  payload: number | boolean | string | GlobalLoadingPayload;
}

const globalReducer = (state: ReduxStateGlobal = initialState, action: UtilAction): ReduxStateGlobal => {
  switch (action.type) {
    case RESET: {
      // Preserve device-level language preference across logout/reset.
      // Tolgee LanguageStorage + PersonalTab mirror; survive local state cleanup.
      const preservedTolgeeLanguage = localStorage.getItem('__tolgee_currentLanguage');
      const preservedI18nextLng = localStorage.getItem('i18nextLng');
      localStorage.clear();
      if (preservedTolgeeLanguage !== null) {
        localStorage.setItem('__tolgee_currentLanguage', preservedTolgeeLanguage);
      }
      if (preservedI18nextLng !== null) {
        localStorage.setItem('i18nextLng', preservedI18nextLng);
      }
      return initialState;
    }
    case GLOBAL_LOADING: {
      const payload = action.payload as GlobalLoadingPayload;
      const newLoadingCounter = Math.max(0, state.loadingCounter + payload.counter);

      let newLoadingType = payload.loadingType || state.loadingType;

      if (newLoadingCounter == 0) {
        newLoadingType = undefined;
      }

      return {...state, loadingCounter: newLoadingCounter, loadingType: newLoadingType};
    }
    default:
      return state;
  }
};

export default globalReducer;

export const reducerName = 'global';

const selectLoadingCounter = (state: ReduxState): number => state[reducerName].loadingCounter;
const selectLoadingType = (state: ReduxState): GlobalLoadingType | undefined => state[reducerName].loadingType;

export const getUtilStateIsLoading = createSelector([selectLoadingCounter], (loadingCounter) => loadingCounter > 0);
export const getGlobalLoadingType = createSelector([selectLoadingType], (loadingType) => loadingType);
